import { Treatment } from "@redotech/redo-model/split";
import {
  AttachmentStrategy,
  DynamicReturnTypes,
} from "@redotech/redo-model/team";
import {
  feeProduct,
  WidgetAutocheck,
  WidgetFees,
} from "@redotech/redo-model/widget";
import { getPricingRulesInLocalCurrency } from "@redotech/server/utils/coverage-calculator/calculator-utils";

export function pickTreatment(
  treatments: Treatment[],
  localStorageTreatmentId: string | null,
  urlTreamentID: string | null,
): Treatment {
  let treatment: Treatment | undefined;

  if (urlTreamentID) {
    treatment = treatments.find((t) => t._id === urlTreamentID);
  }
  if (treatment) return treatment;

  if (localStorageTreatmentId) {
    treatment = treatments.find((t) => t._id === localStorageTreatmentId);
  }
  if (treatment) return treatment;

  // randomly pick treatment with weights
  let weightSum = treatments.reduce((sum, t) => (sum += t.weight), 0);

  const threshold = Math.random() * weightSum;

  weightSum = 0;
  for (let i = 0; i < treatments.length; ++i) {
    // Add the weight to our running total.
    weightSum += treatments[i].weight;
    // select treatment

    if (weightSum >= threshold) {
      return treatments[i];
    }
  }

  // will never reach this
  return treatments[0];
}

export function applyTreatment({
  treatment,
  autocheck,
  fees,
}: {
  treatment: Treatment;
  autocheck: WidgetAutocheck;
  fees?: WidgetFees;
}) {
  // edit all safe choices
  autocheck.hideCheckboxBranding = treatment.settings.hideBranding;
  autocheck.packageProtectionAutoCheck =
    treatment.settings.packageProtectionAutoEnable;
  autocheck.redoAutoCheck = treatment.settings.redoAutoEnable;
  autocheck.hideCheckbox = treatment.settings.hideOnPDP;
  autocheck.toggleTextOptions =
    treatment.settings.toggleTextOptions ?? autocheck.toggleTextOptions;
  autocheck.attachmentStrategy = treatment.settings.attachmentStrategy;
  autocheck.cartToggle.cartToggleSelector =
    treatment.settings.cartElementPlacementSelector ??
    autocheck.cartToggle.cartToggleSelector;
  autocheck.cartToggle.cartClickListenerSelector =
    treatment.settings.cartListenerSelector ??
    autocheck.cartToggle.cartClickListenerSelector;
  autocheck.cartToggle.cartTogglePlacement =
    treatment.settings.cartElementPlacement ??
    autocheck.cartToggle.cartTogglePlacement;
  autocheck.offerDiscountOnNextOrder =
    treatment.settings.offerDiscountOnNextOrder ?? false;
  autocheck.showIcons = treatment.settings.showIcons ?? false;
  autocheck.offerDiscountOnNextOrderPercentage =
    treatment.settings.offerDiscountOnNextOrderPercentage ?? "0";
  autocheck.groupNearbyToggleElementsTogether =
    treatment.settings.groupNearbyToggleElementsTogether;
  if (treatment.settings.termsAndConditions) {
    autocheck.termsAndConditions = treatment.settings.termsAndConditions;
  }
  if (treatment.settings.clickStandardCheckoutButton) {
    autocheck.clickStandardCheckoutButton =
      treatment.settings.clickStandardCheckoutButton;
  }
  if (treatment.settings.forceDoubleCheckoutToggle) {
    autocheck.forceDoubleCheckoutToggle =
      treatment.settings.forceDoubleCheckoutToggle;
  }
  if (treatment.settings.packageProtectionMaxPrice) {
    autocheck.packageProtection.maxPrice =
      +treatment.settings.packageProtectionMaxPrice;
  }
  if (treatment.settings.packageProtectionMinPrice) {
    autocheck.packageProtection.minPrice =
      +treatment.settings.packageProtectionMinPrice;
  }

  if (treatment.settings.cartElementShadowRootPlacementSelector) {
    autocheck.cartToggle.cartToggleShadowRootSelector =
      treatment.settings.cartElementShadowRootPlacementSelector;
  }

  if (treatment.settings.cartElementImage) {
    autocheck.logo = treatment.settings.cartElementImage;
  }

  if (treatment.settings.widgetConfigs) {
    autocheck.widgetConfigs = treatment.settings.widgetConfigs;
  }

  if (autocheck.hideRedoProduct) {
    autocheck.hideRedoProduct.enabled =
      treatment.settings.hideCartProduct ||
      autocheck.hideRedoProduct.enabled ||
      false;
  }

  if (autocheck.hideRedoProduct) {
    autocheck.hideRedoProduct.productSelector =
      treatment.settings.hideCartProductSelector;

    autocheck.hideRedoProduct.checkoutButtonSelector =
      treatment.settings.hideCheckoutButtonSelector;
  }

  if (treatment.settings.usingCartAndCheckoutToggle) {
    autocheck.usingCartAndCheckoutToggle =
      treatment.settings.usingCartAndCheckoutToggle;
  } else {
    autocheck.usingCartAndCheckoutToggle = undefined;
  }

  // set ui experience
  if (treatment.settings.attachmentStrategy === AttachmentStrategy.CHECKBOX) {
    autocheck.cartToggle.enabled = false;
    autocheck.checkoutExtensionEnabled = false;
    autocheck.hideRedoProduct = undefined;
    autocheck.packageProtection.coverageEnabled = false;
  } else if (
    treatment.settings.attachmentStrategy ===
      AttachmentStrategy.SINGLE_TOGGLE ||
    treatment.settings.attachmentStrategy ===
      AttachmentStrategy.DOUBLE_TOGGLE ||
    treatment.settings.attachmentStrategy === AttachmentStrategy.CART_CARD ||
    treatment.settings.attachmentStrategy ===
      AttachmentStrategy.CHECKOUT_BUTTON_PRODUCT ||
    treatment.settings.attachmentStrategy ===
      AttachmentStrategy.CHECKOUT_BUTTON_SHIPPING
  ) {
    autocheck.cartToggle.enabled = true;
    autocheck.checkoutExtensionEnabled = false;
  } else if (
    treatment.settings.attachmentStrategy === AttachmentStrategy.CHECK_OUT ||
    treatment.settings.attachmentStrategy ===
      AttachmentStrategy.DOUBLE_CHECK_OUT
  ) {
    autocheck.checkoutExtensionEnabled = true;
    autocheck.cartToggle.enabled = false;
  }

  if (treatment.settings.customToggleCss) {
    autocheck.customToggleCss = treatment.settings.customToggleCss;
  }

  // returns pricing
  if (treatment.settings.returnOptions?.dynamicPricing) {
    const dynamicPricing: any = treatment.settings.returnOptions.dynamicPricing;

    if (autocheck.currencyExchangeRate) {
      if (
        dynamicPricing &&
        dynamicPricing.type === DynamicReturnTypes.ITEM_COUNT
      ) {
        dynamicPricing.maxPrice =
          dynamicPricing.maxPrice * autocheck.currencyExchangeRate;
        dynamicPricing.pricePerAdditonalItem =
          dynamicPricing.pricePerAdditonalItem * autocheck.currencyExchangeRate;
      } else if (
        dynamicPricing &&
        dynamicPricing.type === DynamicReturnTypes.CART_VALUE
      ) {
        dynamicPricing.maxPrice =
          dynamicPricing.maxPrice * autocheck.currencyExchangeRate;
        dynamicPricing.pricingRuleSet = getPricingRulesInLocalCurrency(
          [dynamicPricing.pricingRuleSet],
          autocheck.currencyExchangeRate,
        )[0];
      }

      if (treatment.settings.returnOptions?.pricePerOrder) {
        treatment.settings.returnOptions.pricePerOrder = (
          parseFloat(treatment.settings.returnOptions.pricePerOrder) *
          autocheck.currencyExchangeRate
        ).toString();
      }
    }

    autocheck.dynamicReturnsPricing = dynamicPricing;
  } else {
    autocheck.dynamicReturnsPricing = undefined;
  }

  autocheck.internationalReturnsPricing =
    treatment.settings.returnOptions?.internationalPricing;
  if (treatment.settings.returnOptions?.serviceCountries) {
    autocheck.countries = treatment.settings.returnOptions.serviceCountries;
  }
  if (treatment.settings.returnOptions?.pricePerOrder) {
    autocheck.pricePerOrder = treatment.settings.returnOptions.pricePerOrder;
    if (fees) {
      fees.price = treatment.settings.returnOptions.pricePerOrder;
    }
  }
  if (treatment.settings.returnOptions?.excludeCoverageIfAnyInvalidProduct) {
    autocheck.excludeCoverageIfAnyInvalidProduct =
      treatment.settings.returnOptions.excludeCoverageIfAnyInvalidProduct;
  }

  // package protection pricing
  if (treatment.settings.packageProtectionPricingRulesets?.length) {
    if (autocheck.currencyExchangeRate) {
      autocheck.packageProtection.pricingRuleSets =
        getPricingRulesInLocalCurrency(
          treatment.settings.packageProtectionPricingRulesets,
          autocheck.currencyExchangeRate,
        );
    } else {
      autocheck.packageProtection.pricingRuleSets =
        treatment.settings.packageProtectionPricingRulesets;
    }
  }

  if (treatment.settings.splitProducts !== undefined) {
    autocheck.packageProtection.splitProducts =
      treatment.settings.splitProducts;
    if (fees) {
      fees.splitProducts = treatment.settings.splitProducts;
    }
  }

  // product controls
  if (
    !treatment.settings.redoEnabled ||
    (!treatment.settings.returnEnabled &&
      !treatment.settings.packageProtectionEnabled)
  ) {
    autocheck.enabled = false;
    autocheck.coverageEnabled = false;
    autocheck.packageProtection.coverageEnabled = false;
  } else if (
    autocheck.coverageEnabled &&
    autocheck.packageProtection.coverageEnabled &&
    autocheck.packageProtection.splitProducts &&
    treatment.settings.attachmentStrategy !== "checkbox"
  ) {
    autocheck.packageProtection.coverageEnabled =
      treatment.settings.packageProtectionEnabled;
    autocheck.coverageEnabled = treatment.settings.returnEnabled;
  }

  if (
    !treatment.settings.returnEnabled &&
    treatment.settings.packageProtectionEnabled &&
    fees?.packageProduct
  ) {
    reduceToggles(autocheck, treatment);
    reduceToOneProduct(fees, fees.packageProduct);
  } else if (
    !treatment.settings.packageProtectionEnabled &&
    treatment.settings.returnEnabled &&
    fees?.returnProduct
  ) {
    reduceToOneProduct(fees, fees.returnProduct);
    reduceToggles(autocheck, treatment);
  }

  return treatment._id;
}

function reduceToggles(autocheck: WidgetAutocheck, treatment: Treatment) {
  if (
    treatment.settings.attachmentStrategy === AttachmentStrategy.DOUBLE_TOGGLE
  ) {
    autocheck.attachmentStrategy = AttachmentStrategy.SINGLE_TOGGLE;
  } else if (
    treatment.settings.attachmentStrategy ===
    AttachmentStrategy.DOUBLE_CHECK_OUT
  ) {
    autocheck.attachmentStrategy = AttachmentStrategy.CHECK_OUT;
  }
  autocheck.packageProtection.splitProducts = false;
}

function reduceToOneProduct(fees: WidgetFees, product: feeProduct) {
  fees.handle = product.handle;
  fees.mainType = product.type;
  fees.productId = product.productId;

  fees.returnProduct = product.type === "return" ? product : undefined;
  fees.packageProduct =
    product.type === "package_protection" ? product : undefined;
  fees.bothProduct = undefined;
  fees.splitProducts = false;
}
